/**
 * Client-side log forwarder. Buffers events and POSTs them to the API
 * (`/v1/log`) so they show up in Workers Observability alongside server
 * logs. Use this for forensics on issues where the user is on a device
 * we can't otherwise inspect (mobile, customer machines).
 *
 * Conventions:
 *   - Don't log anything sensitive. PII-free or hashed.
 *   - Use ctx for structured fields (will be searchable as JSON).
 *   - Prefer `clog.info` for state transitions, `.error` for failures.
 *
 * Flush triggers:
 *   - 5s timer
 *   - batch reaches BATCH_LIMIT
 *   - page visibility hidden / pagehide (sendBeacon, survives unload)
 *   - manual `clog.flush()`
 */

const API_URL = import.meta.env.VITE_API_URL ?? 'https://api.technicalaccounting.net';
const FLUSH_MS = 5_000;
const BATCH_LIMIT = 20;
const MAX_BUFFER = 100; // hard cap, drop oldest beyond

type Level = 'debug' | 'info' | 'warn' | 'error';

interface ClientLogEvent {
  ts: string; // ISO
  level: Level;
  message: string;
  ctx?: Record<string, unknown>;
}

let buffer: ClientLogEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let sessionId: string | null = null;
let started = false;

function ensureSessionId(): string {
  if (!sessionId) {
    sessionId =
      // crypto.randomUUID is available on all modern browsers; tiny fallback otherwise.
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  return sessionId;
}

function record(level: Level, message: string, ctx?: Record<string, unknown>) {
  if (buffer.length >= MAX_BUFFER) {
    buffer.splice(0, buffer.length - MAX_BUFFER + 1);
  }
  buffer.push({ ts: new Date().toISOString(), level, message, ctx });
  if (buffer.length >= BATCH_LIMIT) {
    void flush();
  } else if (!timer) {
    timer = setTimeout(() => {
      timer = null;
      void flush();
    }, FLUSH_MS);
  }
}

async function flush(useBeacon = false): Promise<void> {
  if (buffer.length === 0) return;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const events = buffer;
  buffer = [];

  const body = JSON.stringify({ session_id: ensureSessionId(), events });
  const url = `${API_URL}/v1/log`;

  try {
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (!ok) {
        // sendBeacon refused (rare, e.g., quota); fall through to fetch.
        throw new Error('sendBeacon refused');
      }
      return;
    }
    await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: useBeacon, // hint for unload-time delivery
    });
  } catch {
    // We dropped the events. Don't loop on errors — observability is
    // best-effort.
  }
}

export const clog = {
  debug(message: string, ctx?: Record<string, unknown>): void {
    record('debug', message, ctx);
  },
  info(message: string, ctx?: Record<string, unknown>): void {
    record('info', message, ctx);
  },
  warn(message: string, ctx?: Record<string, unknown>): void {
    record('warn', message, ctx);
  },
  error(message: string, ctx?: Record<string, unknown>): void {
    record('error', message, ctx);
  },
  flush(): Promise<void> {
    return flush(false);
  },
  sessionId(): string {
    return ensureSessionId();
  },
};

/**
 * Wire global error capture + flush-on-unload. Called once from main.tsx.
 * Idempotent.
 */
export function startClog(): void {
  if (started) return;
  started = true;

  ensureSessionId();

  clog.info('app_init', {
    pathname: window.location.pathname,
    href: window.location.href,
    ua: navigator.userAgent,
    vw: window.innerWidth,
    vh: window.innerHeight,
    online: navigator.onLine,
    cookies_enabled: navigator.cookieEnabled,
    referrer: document.referrer || null,
  });

  window.addEventListener('error', (e) => {
    clog.error('window_error', {
      message: e.message,
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
      stack: e.error instanceof Error ? e.error.stack : null,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    clog.error('unhandled_rejection', {
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack : null,
      name: reason instanceof Error ? reason.name : null,
    });
  });

  // visibilitychange + pagehide: best chance to flush before unload.
  // pagehide is more reliable on iOS Safari than unload/beforeunload.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
  window.addEventListener('pagehide', () => {
    void flush(true);
  });
}
